import { type ComponentPropsWithoutRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  ComposerPrimitive,
  INTERNAL,
  MessagePrimitive,
  ThreadPrimitive,
  useAssistantApi,
  useAssistantState,
  useMessage,
  useMessagePartText,
} from '@assistant-ui/react';
import { IconButton } from '@cbhq/cds-web/buttons';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { Text } from '@cbhq/cds-web/typography';

import styles from './Thread.module.css';

type ThreadProps = {
  onClose: () => void;
  isMaximized: boolean;
  onToggleMaximize: () => void;
};

type TextContentProps = {
  text: string;
};

function AssistantIcon() {
  return (
    <Box paddingY={0.5}>
      <Box
        background="bgSecondary"
        padding={0.5}
        borderRadius={100}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        <Icon name="coinbase" color="fgPrimary" size="l" />
      </Box>
    </Box>
  );
}

function WelcomeMessage() {
  return (
    <VStack padding={3} gap={2} alignItems="center" justifyContent="center" height="100%">
      <Box padding={2} display="flex" alignItems="center" justifyContent="center">
        <Icon name="support" color="fg" size="l" />
      </Box>
      <Text as="h4" font="title3" textAlign="center">
        CDP Support Assistant
      </Text>
      <Text as="p" font="body" color="fgMuted" textAlign="center">
        Hi! I&apos;m your AI support assistant. Ask me anything about Coinbase Developer Platform.
      </Text>
    </VStack>
  );
}

function Composer() {
  const api = useAssistantApi();
  const isDisabled = useAssistantState(({ thread }) => thread.isDisabled);

  const handleSend = useCallback(() => {
    if (!isDisabled) {
      api.composer().send();
    }
  }, [api, isDisabled]);

  return (
    <ComposerPrimitive.Root>
      <HStack gap={1} padding={1.5} alignItems="flex-end" className={styles.composerRoot}>
        <Box flexGrow={1}>
          <ComposerPrimitive.Input
            className={styles.composerTextarea}
            placeholder="Type a message..."
            rows={1}
          />
        </Box>
        <Box paddingBottom={0.5}>
          <IconButton
            name="paperAirplane"
            accessibilityLabel="Send message"
            variant="primary"
            compact
            onClick={handleSend}
            disabled={isDisabled}
          />
        </Box>
      </HStack>
    </ComposerPrimitive.Root>
  );
}

function UserMessageText({ text }: TextContentProps) {
  return (
    <Text as="span" font="body" color="fgInverse">
      {text}
    </Text>
  );
}

const userMessageComponents = { Text: UserMessageText };

function UserMessage() {
  return (
    <MessagePrimitive.Root className={styles.messageRoot}>
      <Box className={styles.userMessage}>
        <MessagePrimitive.Content components={userMessageComponents} />
      </Box>
    </MessagePrimitive.Root>
  );
}

function MarkdownLink({ children, ...props }: ComponentPropsWithoutRef<'a'>) {
  return (
    <a target="_blank" rel="noreferrer" {...props}>
      {children}
    </a>
  );
}

const markdownComponents = { a: MarkdownLink };

function MarkdownText() {
  const { text } = INTERNAL.useSmooth(useMessagePartText());
  return (
    <Text as="div" font="body" color="fg" className={styles.markdownContent}>
      <ReactMarkdown components={markdownComponents}>{text}</ReactMarkdown>
    </Text>
  );
}

const assistantContentComponents = { Text: MarkdownText };

function AssistantMessage() {
  const { isLast } = useMessage();

  return (
    <MessagePrimitive.Root className={styles.messageRoot}>
      <MessagePrimitive.If hasContent={false}>
        {isLast && (
          <HStack gap={1} alignItems="flex-start">
            <AssistantIcon />
            <Box className={styles.assistantMessage}>
              <div className={styles.typingIndicator}>
                <span />
                <span />
                <span />
              </div>
            </Box>
          </HStack>
        )}
      </MessagePrimitive.If>

      <MessagePrimitive.If hasContent>
        <HStack gap={1} alignItems="flex-start">
          <AssistantIcon />
          <Box className={styles.assistantMessage}>
            <MessagePrimitive.Content components={assistantContentComponents} />
          </Box>
        </HStack>
      </MessagePrimitive.If>
    </MessagePrimitive.Root>
  );
}

const threadMessageComponents = { UserMessage, AssistantMessage };

export function Thread({ onClose, isMaximized, onToggleMaximize }: ThreadProps) {
  return (
    <ThreadPrimitive.Root className={styles.threadRoot}>
      <HStack
        padding={2}
        justifyContent="space-between"
        alignItems="center"
        background="bg"
        className={styles.header}
      >
        <HStack gap={1} alignItems="center">
          <Icon name="support" color="fgPositive" size="m" />
          <Text as="h3" font="title3" color="fg">
            AI Support
          </Text>
        </HStack>
        <HStack gap={1}>
          <IconButton
            name={isMaximized ? 'collapse' : 'expand'}
            accessibilityLabel={isMaximized ? 'Minimize chat' : 'Maximize chat'}
            variant="secondary"
            onClick={onToggleMaximize}
            compact
          />
          <IconButton
            name="close"
            accessibilityLabel="Close chat"
            variant="secondary"
            onClick={onClose}
            compact
          />
        </HStack>
      </HStack>

      <ThreadPrimitive.Viewport className={styles.viewport}>
        <ThreadPrimitive.Empty>
          <WelcomeMessage />
        </ThreadPrimitive.Empty>

        <ThreadPrimitive.Messages components={threadMessageComponents} />
      </ThreadPrimitive.Viewport>

      <Composer />
    </ThreadPrimitive.Root>
  );
}
